import { useState, useEffect } from 'react'
import { supabase } from '../supabaseClient'

export interface PriceBadgeData {
  isEligible: boolean
  maxHistoricalPrice: number | null
}

export type PriceBadgeMap = Record<string, PriceBadgeData>

/**
 * Single query for all properties — returns max historical price per property.
 * Replaces per-card usePriceBadgeEligibility to avoid N+1 queries.
 */
export const usePriceBadgeBatch = (
  properties: { id: string; total_price?: number | string; current_price?: number | string }[]
): { badgeMap: PriceBadgeMap; isLoaded: boolean } => {
  const [badgeMap, setBadgeMap] = useState<PriceBadgeMap>({})
  const [isLoaded, setIsLoaded] = useState(false)

  const propertyIds = properties.map(p => p.id).sort().join(',')

  useEffect(() => {
    if (properties.length === 0) {
      setIsLoaded(true)
      return
    }

    const ids = properties.map(p => p.id)

    supabase
      .from('property_price_versions')
      .select('property_id, total_price')
      .in('property_id', ids)
      .order('total_price', { ascending: false })
      .then(({ data, error }) => {
        if (error) {
          console.error('[usePriceBadgeBatch] query failed:', error.message)
          setIsLoaded(true)
          return
        }

        const maxByProperty: Record<string, number> = {}
        for (const row of data ?? []) {
          const pid = row.property_id as string
          const price = Number(row.total_price)
          if (!(pid in maxByProperty) || price > maxByProperty[pid]) {
            maxByProperty[pid] = price
          }
        }

        const result: PriceBadgeMap = {}
        for (const p of properties) {
          const currentPrice = Number(p.total_price ?? p.current_price ?? 0)
          const maxHistorical = maxByProperty[p.id] ?? null
          const isEligible = maxHistorical !== null && maxHistorical > currentPrice
          result[p.id] = {
            isEligible,
            maxHistoricalPrice: isEligible ? maxHistorical : null
          }
        }

        setBadgeMap(result)
        setIsLoaded(true)
      })
  }, [propertyIds])

  return { badgeMap, isLoaded }
}
